import React, { useEffect, useState } from "react";
import { UsersRound, Plus, KeyRound, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { TableSkeleton } from "@/components/common/Loading";
import { useAuth } from "@/context/AuthContext";

const inputCls = "h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200";

export default function Team() {
  const { user } = useAuth();
  const canManage = user?.role === "client_admin";
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => { const { data } = await api.get("/team"); setRows(arr(data)); };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const { data } = await api.post("/team", form); setCreated(data.credentials); setOpen(false); setForm({ name: "", email: "", username: "", password: "", role: "staff" }); toast.success("Team member added"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const reset = async (id) => {
    try { const { data } = await api.post(`/team/${id}/reset-password`); setCreated(data.credentials); toast.success("Password reset"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async () => {
    try { await api.delete(`/team/${delTarget.id}`); toast.success("Removed"); setDelTarget(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Team" subtitle="Manage staff and viewer accounts" icon={UsersRound}
        actions={canManage && <Button className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" onClick={() => setOpen(true)} data-testid="add-team-btn"><Plus className="mr-1 h-4 w-4" /> Add User</Button>} />

      {!rows ? <TableSkeleton /> : (
        <div className="card-surface overflow-x-auto p-0 scrollbar-thin">
          <table className="w-full min-w-[600px]">
            <thead><tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Username</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((u) => (
                <tr key={u.id} className="text-sm">
                  <td className="px-4 py-3"><p className="font-semibold text-slate-800">{u.name}</p><p className="text-xs text-slate-400">{u.email}</p></td>
                  <td className="px-4 py-3 text-slate-600">{u.username}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{u.role.replace("_", " ")}</td>
                  <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {canManage && u.role !== "client_admin" && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => reset(u.id)} title="Reset password"><KeyRound className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDelTarget(u)} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-3">
            <input className={inputCls} placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className={inputCls} placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <input className={inputCls} placeholder="Username *" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
            <input className={inputCls} placeholder="Temporary password *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent>
            </Select>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-xl bg-brand-700 text-white hover:bg-brand-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle>Login details</DialogTitle></DialogHeader>
          {created && (
            <div className="rounded-xl bg-slate-50 p-4 text-sm">
              <p className="text-slate-500">Username</p><p className="font-mono font-semibold">{created.username}</p>
              <p className="mt-2 text-slate-500">Temporary Password</p><p className="font-mono font-semibold">{created.password}</p>
              <p className="mt-3 text-xs text-amber-600">User must change this on first login.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={() => setDelTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle>Remove {delTarget?.name}?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this team member's account.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="rounded-xl bg-red-600 hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
